import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { buscarDetalhesContaCorrente } from "../../../services/api-conta-corrente.service";
import { formatValue, formatCurrency } from "../../../utils/formatValue";
import styles from "./styles.module.css";
import Loading from "../../../components/Loading";
import SnackBar from "../../../components/SnackBar";
import ModalLancarDebito from "./ModalLancarDebito";
import BlueButton from "../../../components/BlueButton";
import WhiteButton from "../../../components/WhiteButton";
import { CardSecao, RodapeFixo } from "../../../components/PadraoTela";
import BreadcrumbApuracao from "../../Apuracoes/components/BreadcrumbApuracao";

const ContaCorrenteDetalhes = () => {
  const { cnpj } = useParams();
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [modalDebito, setModalDebito] = useState(false);

  const handleCarregarDados = useCallback(async () => {
    setLoading(true);
    try {
      console.log("Buscando detalhes para CNPJ:", cnpj);
      const response = await buscarDetalhesContaCorrente(cnpj);
      console.log("Resposta completa da API de detalhes:", JSON.stringify(response, null, 2));

      // O interceptor do axios já retorna response.data, então response já é o objeto { success, data }
      // Mas pode ser que a resposta venha diretamente como { success: true, data: {...} }
      let dadosApi = null;
      
      if (response && response.success && response.data) {
        // Resposta no formato { success: true, data: {...} }
        dadosApi = response.data;
      } else if (response && response.cnpj) {
        // Resposta direta com os dados (caso o interceptor tenha retornado apenas os dados)
        dadosApi = response;
      } else if (response && response.data && !response.success) {
        // Resposta pode ter vindo como { data: {...} }
        dadosApi = response.data;
      }
      
      if (dadosApi) {
        // Ajustar estrutura para compatibilidade com o componente
        // Se houver prévias separadas, usar elas; senão, usar lançamentos diretos
        const grupos = Array.isArray(dadosApi.grupos) 
          ? dadosApi.grupos.map((grupo) => {
              // Se houver prévias, processar cada prévia separadamente
              let lancamentos = [];

              console.log('=== DEBUG GRUPO ===', {
                grupo: grupo.grupo,
                temPrevia: Array.isArray(grupo.previas) && grupo.previas.length > 0,
                totalPrevia: Array.isArray(grupo.previas) ? grupo.previas.length : 0,
                temLancamentos: Array.isArray(grupo.lancamentos) && grupo.lancamentos.length > 0,
                totalLancamentos: Array.isArray(grupo.lancamentos) ? grupo.lancamentos.length : 0,
              });

              if (Array.isArray(grupo.previas) && grupo.previas.length > 0) {
                console.log('=== PROCESSANDO PRÉVIAS (e depois lançamentos diretos) ===', {
                  totalPrevia: grupo.previas.length,
                  totalLancamentosDiretos: Array.isArray(grupo.lancamentos) ? grupo.lancamentos.length : 0,
                });

                // 1) Processar cada prévia e seus lançamentos
                grupo.previas.forEach((previa, idxPrevia) => {
                  console.log(`=== DEBUG PRÉVIA ${idxPrevia} ===`, {
                    idPrevia: previa.idPrevia,
                    idPreviaVersao: previa.idPreviaVersao,
                    projeto_codigo: previa.projeto_codigo,
                    totalLancamentos: Array.isArray(previa.lancamentos) ? previa.lancamentos.length : 0,
                  });

                  if (Array.isArray(previa.lancamentos) && previa.lancamentos.length > 0) {
                    previa.lancamentos.forEach((l, idxLanc) => {
                      const metadataCompleto = {
                        ...(l.metadata || {}),
                        idPrevia: previa.idPrevia,
                        idPreviaVersao: previa.idPreviaVersao,
                        projeto_codigo: previa.projeto_codigo || l.metadata?.projeto_codigo || l.metadata?.projeto,
                      };

                      console.log(`=== DEBUG LANÇAMENTO ${idxLanc} DA PRÉVIA ${idxPrevia} ===`, {
                        id: l.id,
                        nome: l.nome || l.descricao,
                        idPrevia: metadataCompleto.idPrevia,
                        valor: l.valor,
                      });

                      lancamentos.push({
                        id: l.id,
                        nome: l.nome || l.descricao || '-',
                        tipo: l.tipo || '-',
                        valor: l.valor || 0,
                        data: l.data || '',
                        mes_referencia: l.mes_referencia || l.metadata?.mes_referencia || '',
                        metadata: metadataCompleto,
                      });
                    });
                  }
                });

                // 2) Adicionar também lançamentos diretos do grupo (prévia antiga sem idPrevia)
                if (Array.isArray(grupo.lancamentos) && grupo.lancamentos.length > 0) {
                  console.log('=== ADICIONANDO LANÇAMENTOS DIRETOS (sem idPrevia) ===', {
                    total: grupo.lancamentos.length,
                  });

                  grupo.lancamentos.forEach((l) => {
                    const metadataLanc = l.metadata || {};
                    lancamentos.push({
                      id: l.id,
                      nome: l.nome || l.descricao || '-',
                      tipo: l.tipo || '-',
                      valor: l.valor || 0,
                      data: l.data || '',
                      mes_referencia: l.mes_referencia || metadataLanc.mes_referencia || '',
                      metadata: metadataLanc,
                    });
                  });
                }
              } else {
                // Só processar grupo.lancamentos se NÃO houver prévias
                console.log('=== PROCESSANDO LANÇAMENTOS DIRETOS (sem prévias) ===');

                if (Array.isArray(grupo.lancamentos) && grupo.lancamentos.length > 0) {
                  console.log('=== DEBUG LANÇAMENTOS SEM PRÉVIA ===', {
                    total: grupo.lancamentos.length,
                  });

                  grupo.lancamentos.forEach((l) => {
                    const metadataLanc = l.metadata || {};
                    const idPreviaDoMetadata = metadataLanc.idPrevia;

                    console.log('=== DEBUG LANÇAMENTO SEM PRÉVIA ===', {
                      id: l.id,
                      nome: l.nome || l.descricao,
                      idPreviaDoMetadata: idPreviaDoMetadata,
                      metadata: metadataLanc,
                    });

                    lancamentos.push({
                      id: l.id,
                      nome: l.nome || l.descricao || '-',
                      tipo: l.tipo || '-',
                      valor: l.valor || 0,
                      data: l.data || '',
                      mes_referencia: l.mes_referencia || metadataLanc.mes_referencia || '',
                      metadata: metadataLanc,
                    });
                  });
                }
              }

              console.log('=== DEBUG TOTAL LANÇAMENTOS PROCESSADOS ===', {
                grupo: grupo.grupo,
                total: lancamentos.length,
                idsPrevia: [...new Set(lancamentos.map(l => l.metadata?.idPrevia).filter(Boolean))],
              });

              // Verificar se tem prévias para passar flag ao renderTabelaLancamentos
              const temPrevia = Array.isArray(grupo.previas) && grupo.previas.length > 0;
              
              return {
                tipo: grupo.grupo,
                nome: grupo.grupo === 'federal' ? 'Impostos Federais' : 
                      grupo.grupo === 'estadual' ? 'Impostos Estaduais' : 
                      'Impostos Municipais',
                icone: grupo.grupo === 'federal' ? '🏛️' : 
                       grupo.grupo === 'estadual' ? '🏢' : 
                       '🏙️',
                saldo: grupo.saldo || 0,
                lancamentos: lancamentos,
                previas: grupo.previas || undefined, // Incluir prévias para referência
                temPrevia: temPrevia, // Flag para indicar se tem prévias
              };
            })
          : [];
        
        const dadosFormatados = {
          cliente: {
            cnpj: dadosApi.cnpj || cnpj,
            razao_social: dadosApi.razao_social || '-',
            status: dadosApi.status || 'inativo',
            ultima_atualizacao: dadosApi.ultima_atualizacao || new Date().toISOString(),
          },
          saldo_total: dadosApi.saldo_total || 0,
          grupos: grupos,
        };

        console.log("Dados formatados:", dadosFormatados);
        setDados(dadosFormatados);
      } else {
        console.error("Resposta inválida da API:", response);
        setObjSnackBar({
          type: "error",
          msg: "Erro ao carregar dados. Resposta inválida da API.",
        });
      }
    } catch (error) {
      console.error("Erro ao buscar detalhes:", error);
      console.error("Detalhes do erro:", {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
      });
      setObjSnackBar({
        type: "error",
        msg: error.response?.data?.mensagem ?? error?.message ?? "Ops, erro ao carregar dados.",
      });
    } finally {
      setLoading(false);
    }
  }, [cnpj]);

  useEffect(() => {
    if (cnpj) {
      handleCarregarDados();
    } else {
      console.error("CNPJ não encontrado nos parâmetros da URL");
      setObjSnackBar({
        type: "error",
        msg: "CNPJ não encontrado na URL.",
      });
      setLoading(false);
    }
  }, [cnpj, handleCarregarDados]);

  const formatStatus = (status) => {
    switch (status) {
      case "ativo":
        return "✅ Ativo";
      case "bloqueado":
        return "🟠 Bloqueado";
      case "inativo":
        return "🔴 Inativo";
      default:
        return status;
    }
  };

  const ordenarLancamentosCronologicamente = (lancamentos) => {
    return [...lancamentos].sort((a, b) => {
      const dataA = new Date(a.data);
      const dataB = new Date(b.data);
      return dataA - dataB;
    });
  };

  const calcularTotais = (lancamentos) => {
    const creditos = lancamentos
      .filter((l) => l.tipo === "credito")
      .reduce((sum, l) => sum + parseFloat(l.valor), 0);
    const debitos = lancamentos
      .filter((l) => l.tipo === "debito")
      .reduce((sum, l) => sum + parseFloat(l.valor), 0);
    return { creditos, debitos };
  };

  const renderTabelaLancamentos = (lancamentos, grupoComPrevia = false, previas = []) => {
    // Se o grupo já veio com prévias separadas do backend, agregar cada prévia em uma linha
    if (grupoComPrevia && previas && previas.length > 0) {
      // Agregar cada prévia em uma única linha
      const lancamentosAgregados = previas.map((previa) => {
        const lancamentosPrevia = previa.lancamentos || [];
        
        // Calcular range de período baseado nos mes_referencia reais de cada lançamento
        const parseRef = (l) => {
          // Priorizar mes_referencia direto (formato YYYY-MM)
          if (l.mes_referencia && typeof l.mes_referencia === 'string' && l.mes_referencia.match(/^\d{4}-\d{2}$/)) {
            return l.mes_referencia;
          }
          // Se não tiver mes_referencia válido, tentar extrair da data
          if (!l.data) return null;
          const d = new Date(l.data);
          if (Number.isNaN(d.getTime())) return null;
          const ano = d.getFullYear();
          const mes = String(d.getMonth() + 1).padStart(2, "0");
          return `${ano}-${mes}`;
        };

        // Extrair todos os períodos válidos dos lançamentos desta prévia
        const referenciasOrdenadas = lancamentosPrevia
          .map((l) => parseRef(l))
          .filter(Boolean)
          .sort();

        const refInicio = referenciasOrdenadas[0];
        const refFim = referenciasOrdenadas[referenciasOrdenadas.length - 1];

        const periodoInicio = refInicio
          ? (() => {
              const [ano, mes] = refInicio.split("-");
              return `${mes}/${ano}`;
            })()
          : null;

        const periodoFim = refFim
          ? (() => {
              const [ano, mes] = refFim.split("-");
              return `${mes}/${ano}`;
            })()
          : null;

        const periodoRange =
          periodoInicio && periodoFim
            ? `${periodoInicio} a ${periodoFim}`
            : periodoInicio || periodoFim || "";

        const totalPrevia = lancamentosPrevia.reduce(
          (sum, l) => sum + parseFloat(l.valor || 0),
          0
        );

        const primeiro = lancamentosPrevia[0] || {};
        
        // Padrão: sempre usar projeto_codigo quando disponível, sem parênteses
        const nomePrevia = `INSS${previa.projeto_codigo ? ` - ${previa.projeto_codigo}` : ''}`;

        return {
          id: `INSS-PREVIA-${previa.idPrevia || previa.projeto_codigo || 'SEM-ID'}`,
          nome: nomePrevia,
          tipo: "credito",
          valor: totalPrevia,
          data: primeiro.data || null,
          mes_referencia: periodoRange,
          metadata: {
            ...(primeiro.metadata || {}),
            periodo_range: periodoRange,
            idPrevia: previa.idPrevia,
            idPreviaVersao: previa.idPreviaVersao,
            projeto_codigo: previa.projeto_codigo,
          },
        };
      });
      
      // Agregar também os lançamentos que estão em grupo.lancamentos (prévia antiga sem idPrevia)
      // Agrupar por projeto_codigo ou external_event_id
      const lancamentosSemPrevia = lancamentos.filter((l) => {
        // Verificar se este lançamento já foi incluído em alguma prévia
        return !previas.some((previa) => {
          const lancamentosPrevia = previa.lancamentos || [];
          return lancamentosPrevia.some((lp) => lp.id === l.id);
        });
      });
      
      if (lancamentosSemPrevia.length > 0) {
        // Agrupar por projeto_codigo ou external_event_id
        const lancamentosPorProjeto = new Map();
        
        lancamentosSemPrevia.forEach((l) => {
          const isInssCredito = l.metadata?.sistema_origem === "api-inss" || (l.nome || "").toUpperCase().includes("INSS");
          
          if (isInssCredito && l.tipo === "credito") {
            // Extrair chave de agrupamento: projeto_codigo ou external_event_id (sem o período)
            const projeto = l.metadata?.projeto_codigo || l.metadata?.projeto || l.projeto_codigo;
            const externalId = l.metadata?.external_event_id || l.external_event_id;
            
            // Se tiver external_event_id, extrair a parte antes do último hífen (ex: "inss-previa-446-2019-04" -> "inss-previa-446")
            let chaveAgrupamento = projeto;
            if (externalId && typeof externalId === 'string') {
              const parts = externalId.split('-');
              if (parts.length >= 3) {
                // Pegar tudo exceto o último (período)
                chaveAgrupamento = parts.slice(0, -1).join('-');
              }
            }
            
            if (!chaveAgrupamento) {
              chaveAgrupamento = 'sem-projeto';
            }
            
            if (!lancamentosPorProjeto.has(chaveAgrupamento)) {
              lancamentosPorProjeto.set(chaveAgrupamento, {
                projeto_codigo: projeto,
                lancamentos: [],
              });
            }
            
            lancamentosPorProjeto.get(chaveAgrupamento).lancamentos.push(l);
          }
        });
        
        // Agregar cada projeto em uma linha
        lancamentosPorProjeto.forEach((projetoData) => {
          const lancamentosProjeto = projetoData.lancamentos;
          
          // Calcular range de período baseado nos mes_referencia reais de cada lançamento
          const parseRef = (l) => {
            // Priorizar mes_referencia direto (formato YYYY-MM)
            if (l.mes_referencia && typeof l.mes_referencia === 'string' && l.mes_referencia.match(/^\d{4}-\d{2}$/)) {
              return l.mes_referencia;
            }
            // Se não tiver mes_referencia válido, tentar extrair da data
            if (!l.data) return null;
            const d = new Date(l.data);
            if (Number.isNaN(d.getTime())) return null;
            const ano = d.getFullYear();
            const mes = String(d.getMonth() + 1).padStart(2, "0");
            return `${ano}-${mes}`;
          };

          // Extrair todos os períodos válidos dos lançamentos deste projeto
          const referenciasOrdenadas = lancamentosProjeto
            .map((l) => parseRef(l))
            .filter(Boolean)
            .sort();

          const refInicio = referenciasOrdenadas[0];
          const refFim = referenciasOrdenadas[referenciasOrdenadas.length - 1];

          const periodoInicio = refInicio
            ? (() => {
                const [ano, mes] = refInicio.split("-");
                return `${mes}/${ano}`;
              })()
            : null;

          const periodoFim = refFim
            ? (() => {
                const [ano, mes] = refFim.split("-");
                return `${mes}/${ano}`;
              })()
            : null;

          const periodoRange =
            periodoInicio && periodoFim
              ? `${periodoInicio} a ${periodoFim}`
              : periodoInicio || periodoFim || "";

          const totalProjeto = lancamentosProjeto.reduce(
            (sum, l) => sum + parseFloat(l.valor || 0),
            0
          );

          const primeiro = lancamentosProjeto[0] || {};
          
          const nomeProjeto = `INSS${projetoData.projeto_codigo ? ` - ${projetoData.projeto_codigo}` : ''}`;

          lancamentosAgregados.push({
            id: `INSS-PROJETO-${projetoData.projeto_codigo || 'SEM-PROJETO'}`,
            nome: nomeProjeto,
            tipo: "credito",
            valor: totalProjeto,
            data: primeiro.data || null,
            mes_referencia: periodoRange,
            metadata: {
              ...(primeiro.metadata || {}),
              periodo_range: periodoRange,
              projeto_codigo: projetoData.projeto_codigo,
            },
          });
        });
      }
      
      // Adicionar outros lançamentos não-INSS
      const outrosLancamentos = lancamentos.filter((l) => {
        const isInssCredito = l.metadata?.sistema_origem === "api-inss" || (l.nome || "").toUpperCase().includes("INSS");
        return !isInssCredito || l.tipo !== "credito";
      });
      
      const todosLancamentos = [...lancamentosAgregados, ...outrosLancamentos];
      const lancamentosOrdenados = ordenarLancamentosCronologicamente(todosLancamentos);
      const totais = calcularTotais(lancamentosOrdenados);
      
      return (
        <TableContainer component={Paper} className={styles.table_container}>
          <Table className={styles.transactions_table}>
            <TableHead>
              <TableRow>
                <TableCell className={styles.table_header_cell}>
                  Nome do Lançamento
                </TableCell>
                <TableCell
                  align="right"
                  className={styles.table_header_cell}
                  style={{ width: "180px" }}
                >
                  Crédito
                </TableCell>
                <TableCell
                  align="right"
                  className={styles.table_header_cell}
                  style={{ width: "180px" }}
                >
                  Débito
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lancamentosOrdenados.map((lancamento) => (
                <TableRow key={lancamento.id} className={styles.table_row}>
                  <TableCell>
                    <div className={styles.transaction_name}>
                      {lancamento.nome}
                    </div>
                    <div className={styles.transaction_date}>
                      {lancamento.metadata?.periodo_range
                        ? lancamento.metadata.periodo_range
                        : lancamento.mes_referencia 
                        ? (() => {
                            const [ano, mes] = lancamento.mes_referencia.split('-');
                            return mes && ano ? `${mes}/${ano}` : lancamento.mes_referencia;
                          })()
                        : formatValue("data", lancamento.data)}
                    </div>
                    <div className={styles.transaction_project}>
                      {lancamento.tipo === "credito"
                        ? `Projeto: ${lancamento.metadata?.projeto || lancamento.metadata?.projeto_codigo || 'N/A'}`
                        : (() => {
                            const documento = lancamento.metadata?.darf || lancamento.metadata?.gnre || lancamento.metadata?.guia;
                            const periodo = lancamento.metadata?.periodo || lancamento.mes_referencia;
                            
                            if (documento && periodo) {
                              return `${documento} • Período: ${periodo}`;
                            } else if (periodo) {
                              return `Período: ${periodo}`;
                            } else if (documento) {
                              return documento;
                            }
                            return '';
                          })()}
                    </div>
                  </TableCell>
                  <TableCell align="right">
                    {lancamento.tipo === "credito" ? (
                      <span className={styles.value_credito}>
                        {formatCurrency(lancamento.valor)}
                      </span>
                    ) : (
                      <span className={styles.value_empty}>—</span>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {lancamento.tipo === "debito" ? (
                      <span className={styles.value_debito}>
                        {formatCurrency(lancamento.valor)}
                      </span>
                    ) : (
                      <span className={styles.value_empty}>—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className={styles.table_footer}>
                <TableCell className={styles.table_footer_cell}>
                  <strong>TOTAIS</strong>
                </TableCell>
                <TableCell align="right" className={styles.table_footer_cell}>
                  <strong className={styles.total_credito}>
                    {formatCurrency(totais.creditos)}
                  </strong>
                </TableCell>
                <TableCell align="right" className={styles.table_footer_cell}>
                  <strong className={styles.total_debito}>
                    {formatCurrency(totais.debitos)}
                  </strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      );
    }
    
    // Se NÃO veio com prévias, fazer agrupamento manual (compatibilidade com lançamentos antigos)
    // Agregar créditos de INSS por PRÉVIA (não todos juntos)
    // Isso permite separar créditos de prévias diferentes
    const isInssCredito = (l) => {
      const sistemaOrigem = l.metadata?.sistema_origem || l.metadata?.sistemaOrigem;
      const nomeUpper = (l.nome || "").toUpperCase();
      return l.tipo === "credito" && (sistemaOrigem === "api-inss" || nomeUpper.includes("INSS"));
    };

    const inssCreditos = lancamentos.filter(isInssCredito);
    const outrosLancamentos = lancamentos.filter((l) => !isInssCredito(l));
    
    // Agrupar créditos INSS por prévia (idPrevia)
    const inssPorPrevia = new Map();
    
    inssCreditos.forEach((l) => {
      // Extrair idPrevia do metadata (pode estar em diferentes lugares)
      const idPrevia = l.metadata?.idPrevia || l.metadata?.id_previa || null;
      const chavePrevia = idPrevia ? `previa-${idPrevia}` : 'sem-previa';

      if (!inssPorPrevia.has(chavePrevia)) {
        inssPorPrevia.set(chavePrevia, {
          idPrevia: idPrevia || null,
          idPreviaVersao: l.metadata?.idPreviaVersao || l.metadata?.id_previa_versao || null,
          projeto_codigo: l.metadata?.projeto_codigo || l.metadata?.projeto || null,
          lancamentos: [],
        });
      }
      
      inssPorPrevia.get(chavePrevia).lancamentos.push(l);
    });

    // Criar lançamento agregado para cada prévia
    const lancamentosInssAgregados = Array.from(inssPorPrevia.values()).map((previaData) => {
      const lancamentosPrevia = previaData.lancamentos;
      
      // Descobrir range de período (YYYY-MM)
      const parseRef = (l) => {
        if (l.mes_referencia) return l.mes_referencia;
        if (!l.data) return null;
        const d = new Date(l.data);
        if (Number.isNaN(d.getTime())) return null;
        const ano = d.getFullYear();
        const mes = String(d.getMonth() + 1).padStart(2, "0");
        return `${ano}-${mes}`;
      };

      const referenciasOrdenadas = lancamentosPrevia
        .map((l) => parseRef(l))
        .filter(Boolean)
        .sort();

      const refInicio = referenciasOrdenadas[0];
      const refFim = referenciasOrdenadas[referenciasOrdenadas.length - 1];

      const periodoInicio = refInicio
        ? (() => {
            const [ano, mes] = refInicio.split("-");
            return `${mes}/${ano}`;
          })()
        : null;

      const periodoFim = refFim
        ? (() => {
            const [ano, mes] = refFim.split("-");
            return `${mes}/${ano}`;
          })()
        : null;

      const periodoRange =
        periodoInicio && periodoFim
          ? `${periodoInicio} a ${periodoFim}`
          : periodoInicio || periodoFim || "";

      const totalInss = lancamentosPrevia.reduce(
        (sum, l) => sum + parseFloat(l.valor || 0),
        0
      );

      const primeiro = lancamentosPrevia[0];
      
      // Nome do lançamento: incluir número da prévia se houver
      const nomePrevia = previaData.idPrevia 
        ? `INSS - Prévia #${previaData.idPrevia}${previaData.projeto_codigo ? ` (${previaData.projeto_codigo})` : ''}`
        : 'INSS';

      return {
        id: `INSS-AGREGADO-${previaData.idPrevia || 'SEM-PREVIA'}`,
        nome: nomePrevia,
        tipo: "credito",
        valor: totalInss,
        data: primeiro.data || null,
        mes_referencia: "", // usaremos periodo_range abaixo
        metadata: {
          ...(primeiro.metadata || {}),
          periodo_range: periodoRange,
          idPrevia: previaData.idPrevia,
          idPreviaVersao: previaData.idPreviaVersao,
          projeto_codigo: previaData.projeto_codigo,
        },
      };
    });
    
    let lancamentosParaRender = [...lancamentosInssAgregados, ...outrosLancamentos];

    if (inssCreditos.length > 0) {
      // Descobrir range de período (YYYY-MM)
      const parseRef = (l) => {
        if (l.mes_referencia) return l.mes_referencia;
        if (!l.data) return null;
        const d = new Date(l.data);
        if (Number.isNaN(d.getTime())) return null;
        const ano = d.getFullYear();
        const mes = String(d.getMonth() + 1).padStart(2, "0");
        return `${ano}-${mes}`;
      };

      const referenciasOrdenadas = inssCreditos
        .map((l) => parseRef(l))
        .filter(Boolean)
        .sort();

      const refInicio = referenciasOrdenadas[0];
      const refFim = referenciasOrdenadas[referenciasOrdenadas.length - 1];

      const periodoInicio = refInicio
        ? (() => {
            const [ano, mes] = refInicio.split("-");
            return `${mes}/${ano}`;
          })()
        : null;

      const periodoFim = refFim
        ? (() => {
            const [ano, mes] = refFim.split("-");
            return `${mes}/${ano}`;
          })()
        : null;

      const periodoRange =
        periodoInicio && periodoFim
          ? `${periodoInicio} a ${periodoFim}`
          : periodoInicio || periodoFim || "";

      const totalInss = inssCreditos.reduce(
        (sum, l) => sum + parseFloat(l.valor || 0),
        0
      );

      const primeiro = inssCreditos[0];

      const lancamentoInssAgregado = {
        id: "INSS-AGREGADO",
        nome: "INSS",
        tipo: "credito",
        valor: totalInss,
        data: primeiro.data || null,
        mes_referencia: "", // usaremos periodo_range abaixo
        metadata: {
          ...(primeiro.metadata || {}),
          periodo_range: periodoRange,
        },
      };

      const outrosLancamentos = lancamentos.filter((l) => !isInssCredito(l));
      lancamentosParaRender = [lancamentoInssAgregado, ...outrosLancamentos];
    }

    const lancamentosOrdenados = ordenarLancamentosCronologicamente(lancamentosParaRender);
    const totais = calcularTotais(lancamentosOrdenados);

    return (
      <TableContainer component={Paper} className={styles.table_container}>
        <Table className={styles.transactions_table}>
          <TableHead>
            <TableRow>
              <TableCell className={styles.table_header_cell}>
                Nome do Lançamento
              </TableCell>
              <TableCell
                align="right"
                className={styles.table_header_cell}
                style={{ width: "180px" }}
              >
                Crédito
              </TableCell>
              <TableCell
                align="right"
                className={styles.table_header_cell}
                style={{ width: "180px" }}
              >
                Débito
              </TableCell>
            </TableRow>
          </TableHead>
            <TableBody>
              {lancamentosOrdenados.map((lancamento) => (
              <TableRow key={lancamento.id} className={styles.table_row}>
                <TableCell>
                  <div className={styles.transaction_name}>
                    {lancamento.nome}
                  </div>
                  <div className={styles.transaction_date}>
                    {lancamento.metadata?.periodo_range
                      ? lancamento.metadata.periodo_range
                      : lancamento.mes_referencia 
                      ? (() => {
                          // Formato YYYY-MM para MM/YYYY
                          const [ano, mes] = lancamento.mes_referencia.split('-');
                          return mes && ano ? `${mes}/${ano}` : lancamento.mes_referencia;
                        })()
                      : formatValue("data", lancamento.data)}
                  </div>
                  <div className={styles.transaction_project}>
                    {lancamento.tipo === "credito"
                      ? `Projeto: ${lancamento.metadata?.projeto || lancamento.metadata?.projeto_codigo || 'N/A'}`
                      : (() => {
                          const documento = lancamento.metadata?.darf || lancamento.metadata?.gnre || lancamento.metadata?.guia;
                          const periodo = lancamento.metadata?.periodo || lancamento.mes_referencia;
                          
                          if (documento && periodo) {
                            return `${documento} • Período: ${periodo}`;
                          } else if (periodo) {
                            return `Período: ${periodo}`;
                          } else if (documento) {
                            return documento;
                          }
                          return '';
                        })()}
                  </div>
                </TableCell>
                <TableCell align="right">
                  {lancamento.tipo === "credito" ? (
                    <span className={styles.value_credito}>
                      {formatCurrency(lancamento.valor)}
                    </span>
                  ) : (
                    <span className={styles.value_empty}>—</span>
                  )}
                </TableCell>
                <TableCell align="right">
                  {lancamento.tipo === "debito" ? (
                    <span className={styles.value_debito}>
                      {formatCurrency(lancamento.valor)}
                    </span>
                  ) : (
                    <span className={styles.value_empty}>—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className={styles.table_footer}>
              <TableCell className={styles.table_footer_cell}>
                <strong>TOTAIS</strong>
              </TableCell>
              <TableCell align="right" className={styles.table_footer_cell}>
                <strong className={styles.total_credito}>
                  {formatCurrency(totais.creditos)}
                </strong>
              </TableCell>
              <TableCell align="right" className={styles.table_footer_cell}>
                <strong className={styles.total_debito}>
                  {formatCurrency(totais.debitos)}
                </strong>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return <Loading />;
  }

  if (!dados) {
    return (
      <BreadcrumbApuracao
        itens={[
          { label: "Conta Corrente", onClick: () => navigate("/conta-corrente/extrato") },
          { label: "Extrato" },
        ]}
      >
        <Typography>Nenhum dado encontrado.</Typography>
      </BreadcrumbApuracao>
    );
  }

  const empresaAtiva = dados?.cliente?.status === "ativo";
  const voltarParaLista = () => navigate("/conta-corrente/extrato");
  const rotuloInfo = { fontSize: "0.75rem", color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" };
  const valorInfo = { fontSize: "0.9375rem", color: "#1f2937", fontWeight: 600, mt: 0.25 };

  return (
    <BreadcrumbApuracao
      itens={[
        { label: "Conta Corrente", onClick: voltarParaLista },
        { label: dados.cliente.razao_social },
      ]}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 1.5 }}>
        <ReceiptLongOutlinedIcon sx={{ color: "#18316e", fontSize: 28 }} />
        <Typography
          component="h1"
          variant="h5"
          fontWeight={700}
          sx={{ color: "#18316e", fontSize: { xs: "1.25rem", md: "1.625rem" }, letterSpacing: "-0.3px" }}
        >
          Extrato da Conta Corrente
        </Typography>
      </Box>

      <CardSecao icone={<BusinessOutlinedIcon />} titulo="Dados do Cliente">
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr 1fr 1fr 1.2fr" }, gap: 2, alignItems: "center" }}>
          <Box>
            <Typography sx={rotuloInfo}>Razão Social</Typography>
            <Typography sx={valorInfo}>{dados.cliente.razao_social}</Typography>
          </Box>
          <Box>
            <Typography sx={rotuloInfo}>CNPJ</Typography>
            <Typography sx={valorInfo}>{formatValue("cnpj", dados.cliente.cnpj)}</Typography>
          </Box>
          <Box>
            <Typography sx={rotuloInfo}>Última Atualização</Typography>
            <Typography sx={valorInfo}>{formatValue("data", dados.cliente.ultima_atualizacao)}</Typography>
          </Box>
          <Box>
            <Typography sx={rotuloInfo}>Status</Typography>
            <Typography sx={valorInfo}>{formatStatus(dados.cliente.status)}</Typography>
          </Box>
          <Box sx={{ bgcolor: "#e8f5e9", borderRadius: 2, px: 2, py: 1.25, textAlign: "right" }}>
            <Typography sx={{ ...rotuloInfo, color: "#2e7d32" }}>Saldo total disponível</Typography>
            <Typography sx={{ fontSize: "1.375rem", fontWeight: 700, color: "#2e7d32" }}>
              {formatCurrency(dados.saldo_total)}
            </Typography>
          </Box>
        </Box>
      </CardSecao>

      {/* Grupos de Impostos */}
      {dados.grupos.map((grupo) => (
        <CardSecao
          key={grupo.tipo}
          icone={<span style={{ fontSize: 20, lineHeight: 1 }}>{grupo.icone}</span>}
          titulo={grupo.nome}
          acao={
            <Box sx={{ textAlign: "right" }}>
              <Typography sx={rotuloInfo}>Saldo do Grupo</Typography>
              <Typography sx={{ fontSize: "1.125rem", fontWeight: 700, color: "#2e7d32" }}>
                {formatCurrency(grupo.saldo)}
              </Typography>
            </Box>
          }
          corpoSx={{ p: 0 }}
        >
          {renderTabelaLancamentos(grupo.lancamentos, grupo.temPrevia, grupo.previas)}
        </CardSecao>
      ))}

      <RodapeFixo
        resumo={
          <>
            <Chip
              icon={<AccountBalanceWalletOutlinedIcon sx={{ fontSize: 18 }} />}
              label={`Saldo total: ${formatCurrency(dados.saldo_total)}`}
              size="small"
              sx={{ bgcolor: "#e8f5e9", color: "#2e7d32" }}
            />
            <Chip
              label={empresaAtiva ? "Empresa ativa" : "Empresa inativa: apenas visualização"}
              size="small"
              sx={empresaAtiva ? { bgcolor: "#e3f2fd", color: "#1565c0" } : { bgcolor: "#fff3e0", color: "#e65100" }}
            />
          </>
        }
      >
        <WhiteButton textValue="Voltar" clickFunction={voltarParaLista} />
        <span title={empresaAtiva ? "" : "Empresa inativa. Apenas visualização permitida."}>
          <BlueButton
            textValue="Lançar Débito"
            contentStartIcon={<CreditCardIcon sx={{ fontSize: 18, mr: 0.5 }} />}
            clickFunction={() => setModalDebito(true)}
            disabled={!empresaAtiva}
          />
        </span>
      </RodapeFixo>

      {/* Modal de Lançar Débito */}
      <ModalLancarDebito
        open={modalDebito}
        onClose={() => setModalDebito(false)}
        cnpj={cnpj}
        dadosCliente={dados.cliente}
        grupos={dados.grupos}
        onSuccess={() => {
          setModalDebito(false);
          handleCarregarDados();
          setObjSnackBar({
            type: "success",
            msg: "Débito lançado com sucesso!",
          });
        }}
      />

      <SnackBar
        objSnackBar={objSnackBar}
        setObjSnackBar={setObjSnackBar}
      />
    </BreadcrumbApuracao>
  );
};

export default ContaCorrenteDetalhes;
